import React, { useState, useEffect } from 'react';
// [Web UX 3. csomag (D), 2026-10-04] A kérés-hibák szövege a közös `apiErrorMessage`-en át megy (a szerver magyar
// indoklása / hálózati hibánál magyar mondat / különben „<tartalék> (HTTP n)") — a korábbi FIX
// szövegű ágak eldobták a szerver indoklását. Őr: `utils/__tests__/rawErrorMessages.guard.test.ts`.
import { apiErrorMessage } from '../../utils/apiErrors';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  Divider,
  Fab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import coachAvailabilityService, {
  CalendarCoachAvailabilityDto,
  VenueDto,
} from '../../services/coachAvailabilityService';
import eventService from '../../services/eventService';
import { userService } from '../../services/userService';
import { formatDateToLocal, getTodayLocal } from '../../utils/dateUtils';
import { formatHours } from '../../utils/coachPresence';
import CoachAvailabilityCreateDialog from './CoachAvailabilityCreateDialog';
import CoachAvailabilityEditDialog from './CoachAvailabilityEditDialog';
import CoachAvailabilityDetailsDialog from './CoachAvailabilityDetailsDialog';
import { NAV_BUTTON_TOUCH_SX } from '../Calendar/calendarA11y';

interface CoachAvailabilityDailyTimelineProps {
  selectedDate: string;
  onDateChange?: (date: string) => void;
  venueFilter?: string;
  coachFilter?: string;
  userRole?: string;
}

interface UserDto {
  userId: string;
  name: string;
  userRoleDisplayName: string;
  isActive: boolean;
}

const CoachAvailabilityDailyTimeline: React.FC<CoachAvailabilityDailyTimelineProps> = ({
  selectedDate,
  onDateChange,
  venueFilter,
  coachFilter,
  userRole = ''
}) => {
  const [currentDate, setCurrentDate] = useState(new Date(selectedDate));
  const [dailyAvailabilities, setDailyAvailabilities] = useState<CalendarCoachAvailabilityDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Filter states
  const [venues, setVenues] = useState<VenueDto[]>([]);
  const [coaches, setCoaches] = useState<UserDto[]>([]);
  const [selectedVenue, setSelectedVenue] = useState<string>('');
  const [selectedCoach, setSelectedCoach] = useState<string>('');

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedAvailabilityId, setSelectedAvailabilityId] = useState<string | null>(null);

  const hungaryMonthNames = [
    'Január', 'Február', 'Március', 'Április', 'Május', 'Június',
    'Július', 'Augusztus', 'Szeptember', 'Október', 'November', 'December'
  ];

  const weekDays = ['Vasárnap', 'Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat'];

  // Load initial data on mount
  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    setCurrentDate(new Date(selectedDate));
  }, [selectedDate]);

  useEffect(() => {
    loadDailyAvailabilities();
  }, [currentDate, selectedVenue, selectedCoach]);

  const loadInitialData = async () => {
    try {
      const [venueData, coachData] = await Promise.all([
        eventService.getVenues(),
        userService.getCoaches()
      ]);

      setVenues(Array.isArray(venueData) ? venueData : []);
      setCoaches(Array.isArray(coachData) ? coachData : []);
    } catch (err) {
      console.error('Error loading initial data:', err);
    }
  };

  const loadDailyAvailabilities = async () => {
    try {
      setLoading(true);
      setError('');

      const dateString = formatDateToLocal(currentDate);
      const availabilityData = await coachAvailabilityService.getDayAvailabilities(
        dateString,
        selectedCoach || undefined,
        selectedVenue || undefined
      );

      setDailyAvailabilities(availabilityData);
    } catch (err) {
      console.error('Error loading daily availabilities:', err);
      setError(apiErrorMessage(err, 'Hiba történt az elérhetőségek betöltésekor'));
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
    if (onDateChange) {
      onDateChange(formatDateToLocal(newDate));
    }
  };

  const handleNextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
    if (onDateChange) {
      onDateChange(formatDateToLocal(newDate));
    }
  };

  const handleToday = () => {
    const today = new Date();
    setCurrentDate(today);
    if (onDateChange) {
      onDateChange(formatDateToLocal(today));
    }
  };

  const handleAvailabilityClick = (availabilityId: string) => {
    setSelectedAvailabilityId(availabilityId);
    setDetailsDialogOpen(true);
  };

  const handleEditAvailability = (availabilityId: string) => {
    // [Web 7 / T3, 2026-10-06] A részletező a szerkesztő megnyitásakor BEZÁR (ahogy a heti nézetben —
    // `CoachAvailabilityWeeklyView.tsx` `onEdit` — és az esemény-naptárakban is). Eddig nyitva maradt a
    // szerkesztő alatt, és a szerkesztő zárása után a RÉGI adatot mutatta: MÉRVE az E2E-n
    // (`flow-deleted-entity-edit.spec.ts`), az időközben törölt elérhetőségnél a 409 után is „Aktív"
    // státusszal látszott a már törölt sor.
    setDetailsDialogOpen(false);
    setSelectedAvailabilityId(availabilityId);
    setEditDialogOpen(true);
  };

  const canCreateAvailability = userRole === 'Coach' || userRole === 'Administrator' || userRole === 'Admin';

  // Generate time slots (6:00 - 22:00)
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 6; hour <= 22; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Percpontos kezdés is a hozzá tartozó (egész órás) sorhoz tartozzon — pl. 16:30 a "16:00" sorhoz.
  const getAvailabilitiesForTimeSlot = (time: string) => {
    const slotHour = parseInt(time.split(':')[0], 10);
    return dailyAvailabilities.filter(availability => {
      const startHour = parseInt(availability.startTime.slice(0, 2), 10);
      return startHour === slotHour;
    });
  };

  // Helper function to detect time overlaps between availabilities
  const doAvailabilitiesOverlap = (availability1: CalendarCoachAvailabilityDto, availability2: CalendarCoachAvailabilityDto) => {
    const start1 = new Date(`2000-01-01 ${availability1.startTime}`);
    const end1 = new Date(`2000-01-01 ${availability1.endTime}`);
    const start2 = new Date(`2000-01-01 ${availability2.startTime}`);
    const end2 = new Date(`2000-01-01 ${availability2.endTime}`);

    return start1 < end2 && start2 < end1;
  };

  // Function to calculate column positioning for overlapping availabilities
  const calculateAvailabilityColumns = () => {
    const columns: { [availabilityId: string]: number } = {};
    const maxColumns: { [timeSlot: string]: number } = {};

    // Sort availabilities by start time, then by duration (longer first)
    const sortedAvailabilities = [...dailyAvailabilities].sort((a, b) => {
      const timeComparison = a.startTime.localeCompare(b.startTime);
      if (timeComparison !== 0) return timeComparison;

      // If same start time, prioritize longer duration
      const durationA = calculateDuration(a.startTime.slice(0, 5), a.endTime.slice(0, 5));
      const durationB = calculateDuration(b.startTime.slice(0, 5), b.endTime.slice(0, 5));
      return durationB - durationA;
    });

    // Group availabilities that overlap
    const overlappingGroups: CalendarCoachAvailabilityDto[][] = [];

    for (const availability of sortedAvailabilities) {
      let addedToGroup = false;

      // Try to add to existing group
      for (const group of overlappingGroups) {
        const overlapsWithGroup = group.some(groupAvailability =>
          doAvailabilitiesOverlap(availability, groupAvailability)
        );

        if (overlapsWithGroup) {
          group.push(availability);
          addedToGroup = true;
          break;
        }
      }

      // Create new group if doesn't overlap with any existing group
      if (!addedToGroup) {
        overlappingGroups.push([availability]);
      }
    }

    // Assign column numbers within each overlapping group
    overlappingGroups.forEach(group => {
      const maxColumnsInGroup = group.length;

      group.forEach((availability, index) => {
        columns[availability.availabilityId] = index;

        // Track max columns needed for each time slot this availability covers.
        // Percpontos kezdésnél (pl. 16:30) is az órához tartozó sort ("16:00") kell találni,
        // ezért az órarész alapján számolunk indexet ahelyett, hogy egyezést keresnénk.
        const startTime = availability.startTime.slice(0, 5);
        const endTime = availability.endTime.slice(0, 5);
        const startHour = parseInt(startTime.split(':')[0], 10);
        const startIndex = startHour - 6;
        const duration = calculateDuration(startTime, endTime);

        for (let i = 0; i < duration; i++) {
          const timeSlotIndex = startIndex + i;
          if (timeSlotIndex >= 0 && timeSlotIndex < timeSlots.length) {
            const timeSlot = timeSlots[timeSlotIndex];
            maxColumns[timeSlot] = Math.max(maxColumns[timeSlot] || 0, maxColumnsInGroup);
          }
        }
      });
    });

    return { columns, maxColumns };
  };


  // Órában, törtrésszel (pl. 16:30-18:00 => 1.5) — a percpontos magasság-/pozíciószámításhoz kell.
  const calculateDuration = (startTime: string, endTime: string) => {
    const start = new Date(`2000-01-01 ${startTime}`);
    const end = new Date(`2000-01-01 ${endTime}`);
    const durationMs = end.getTime() - start.getTime();
    return Math.max(0.25, durationMs / (60 * 60 * 1000)); // Duration in hours, minimum 15 perc
  };

  // "HH:mm" -> óra tizedes törtben (pl. "16:30" => 16.5), a percpontos pozícionáláshoz.
  const parseTimeToHours = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours + minutes / 60;
  };

  const formatDateDisplay = (date: Date) => {
    const day = weekDays[date.getDay()];
    const month = hungaryMonthNames[date.getMonth()];
    return `${date.getFullYear()}. ${month} ${date.getDate()}. ${day}`;
  };

  return (
    <Box>
      {/* Header with navigation */}
      <Paper elevation={2} sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
        <Box display="flex" justifyContent="center" alignItems="center" flexWrap="wrap" gap={0.5}>
          <Box display="flex" alignItems="center" gap={{ xs: 0.5, sm: 1 }}>
            {/* [Web UX 9 / T5, 2026-10-13] címke nélküli lapozók → aria-label + telefonon 44 px. Őr: `iconButtonLabels.guard.test.ts`. */}
            <IconButton onClick={handlePreviousDay} size="small" aria-label="Előző nap" sx={NAV_BUTTON_TOUCH_SX}>
              <ChevronLeftIcon />
            </IconButton>

            <Typography variant="h5" sx={{
              textAlign: 'center',
              fontSize: { xs: '1rem', sm: '1.5rem' },
              whiteSpace: 'nowrap',
            }}>
              {formatDateDisplay(currentDate)}
            </Typography>

            <IconButton onClick={handleNextDay} size="small" aria-label="Következő nap" sx={NAV_BUTTON_TOUCH_SX}>
              <ChevronRightIcon />
            </IconButton>

            <IconButton onClick={handleToday} title="Mai nap" size="small" aria-label="Mai nap" sx={NAV_BUTTON_TOUCH_SX}>
              <TodayIcon />
            </IconButton>
          </Box>

          {loading && <CircularProgress size={20} />}
        </Box>
      </Paper>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 150 }, flex: { xs: 1, sm: 'none' } }}>
          <InputLabel>Helyszín</InputLabel>
          <Select
            value={selectedVenue}
            label="Helyszín"
            onChange={(e: SelectChangeEvent) => setSelectedVenue(e.target.value)}
          >
            <MenuItem value="">Összes</MenuItem>
            {venues.map((venue) => (
              <MenuItem key={venue.venueId} value={venue.venueId}>
                {venue.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 150 }, flex: { xs: 1, sm: 'none' } }}>
          <InputLabel>Edző</InputLabel>
          <Select
            value={selectedCoach}
            label="Edző"
            onChange={(e: SelectChangeEvent) => setSelectedCoach(e.target.value)}
          >
            <MenuItem value="">Összes</MenuItem>
            {coaches.map((coach) => (
              <MenuItem key={coach.userId} value={coach.userId}>
                {coach.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Daily Timeline */}
      <Paper elevation={1} sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Napi Elérhetőségek
        </Typography>

        {dailyAvailabilities.length === 0 && !loading ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              Nincsenek elérhetőségek ezen a napon
            </Typography>
          </Box>
        ) : (
          <Box sx={{ position: 'relative' }}>
            {/* Time slots grid */}
            {timeSlots.map((time, index) => (
              <Box key={time}>
                <Box display="flex" alignItems="center" sx={{ height: 60, py: 1 }}>
                  <Box sx={{ minWidth: 80, py: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {time}
                    </Typography>
                  </Box>
                  <Box sx={{ flexGrow: 1, pl: 2 }}>
                    {/* Empty space for time grid */}
                  </Box>
                </Box>
                {index !== timeSlots.length - 1 && <Divider />}
              </Box>
            ))}

            {/* Overlay availabilities */}
            {(() => {
              const { columns, maxColumns } = calculateAvailabilityColumns();

              return dailyAvailabilities.map((availability) => {
                const startTime = availability.startTime.slice(0, 5);
                const endTime = availability.endTime.slice(0, 5);
                const duration = calculateDuration(startTime, endTime); // teljes időtartam órában, törtrésszel

                // Percpontos pozíció: nem kell egész órára esnie a kezdésnek (pl. 16:30).
                const startHours = parseTimeToHours(startTime);
                const endHours = parseTimeToHours(endTime);

                // A látható sávra (06:00-22:00) vágva, vagy a szélére tapasztva — sose null,
                // csak ha a blokk teljes egészében a látható sávon kívül esne.
                // A 22:00-ás SOR is látszik (timeSlots 06..22), tehát a 22:00–23:00 sáv is rajzolható —
                // a 22-es felső korlát a 22:00-kor kezdődő elérhetőséget eltüntette volna (validátor B2).
                const visibleStart = Math.max(startHours, 6);
                const visibleEnd = Math.min(endHours, 23);
                if (visibleEnd <= visibleStart) return null;

                // Calculate position (60px sor + 8px elválasztó = 68px / óra)
                const topOffset = (visibleStart - 6) * 68;
                const height = (visibleEnd - visibleStart) * 68 - 8;

                // Töredék óra magyar tizedesvesszővel („1,5h"), nem angol ponttal — lásd `formatHours`.
                const durationLabel = Number.isInteger(duration) ? `${duration}h` : `${formatHours(duration)}h`;

                // Calculate horizontal positioning
                const columnIndex = columns[availability.availabilityId] || 0;
                const totalColumns = Math.max(...Object.values(maxColumns), 1);
                const availableWidth = 'calc(100% - 120px)'; // Total available width
                const columnWidth = `calc((${availableWidth}) / ${totalColumns})`;
                const leftOffset = `calc(96px + (${availableWidth}) / ${totalColumns} * ${columnIndex})`;

                return (
                  <Tooltip
                    key={availability.availabilityId}
                    title={`${availability.coachName} - ${availability.startTime.slice(0, 5)}-${availability.endTime.slice(0, 5)} - ${availability.venueNames.join(', ')} ${availability.notes ? `- ${availability.notes}` : ''}`}
                  >
                    <Card
                      sx={{
                        position: 'absolute',
                        left: leftOffset,
                        top: topOffset + 8,
                        width: `calc(${columnWidth} - 8px)`, // Subtract some padding between columns
                        maxWidth: totalColumns === 1 ? 400 : 'none', // Only limit width when single column
                        height: height,
                        cursor: 'pointer',
                        '&:hover': { elevation: 4, zIndex: 10 },
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(76, 175, 80, 0.1)'
                            : '#e8f5e8',
                        border: '2px solid',
                        borderColor: 'success.main',
                        borderRadius: '8px',
                        zIndex: 5
                      }}
                      onClick={() => handleAvailabilityClick(availability.availabilityId)}
                    >
                      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <Box>
                          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <PersonIcon fontSize="small" color="primary" />
                            <Typography variant="body2" fontWeight="bold" noWrap color="text.primary">
                              {availability.coachName}
                            </Typography>
                            <Chip
                              size="small"
                              label={durationLabel}
                              sx={{ ml: 'auto', fontSize: '0.7rem', height: 18 }}
                            />
                          </Box>
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <TimeIcon fontSize="small" color="action" />
                            <Typography variant="body2" fontSize="0.75rem" color="text.primary">
                              {availability.startTime.slice(0, 5)} - {availability.endTime.slice(0, 5)}
                            </Typography>
                          </Box>
                          {availability.venueNames.length > 0 && (
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <LocationIcon fontSize="small" color="action" />
                              <Typography variant="body2" fontSize="0.75rem" noWrap color="text.primary">
                                {availability.venueNames.join(', ')}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        {availability.notes && (
                          <Typography variant="body2" fontSize="0.7rem" color="text.secondary" sx={{ fontStyle: 'italic' }} noWrap>
                            {availability.notes}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Tooltip>
                );
              });
            })()}
          </Box>
        )}
      </Paper>

      {/* Floating Action Button for creating availability */}
      {canCreateAvailability && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{ position: 'sticky', bottom: 16, float: 'right', mr: 2, mt: 2, zIndex: 10 }}
          onClick={() => setCreateDialogOpen(true)}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Dialogs */}
      <CoachAvailabilityCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={() => {
          setCreateDialogOpen(false);
          loadDailyAvailabilities();
        }}
        initialDate={formatDateToLocal(currentDate)}
        userRole={userRole}
      />

      <CoachAvailabilityEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={() => {
          setEditDialogOpen(false);
          loadDailyAvailabilities();
        }}
        availabilityId={selectedAvailabilityId}
      />

      <CoachAvailabilityDetailsDialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        onEdit={handleEditAvailability}
        onDelete={() => {
          setDetailsDialogOpen(false);
          loadDailyAvailabilities();
        }}
        availabilityId={selectedAvailabilityId}
      />
    </Box>
  );
};

export default CoachAvailabilityDailyTimeline;